import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DialogsAPI from 'api/DialogsAPI';
import { GUEST_ROUTES } from 'routes/routes';
import { useLazyGetDialogByScenarioAliasQuery, useLazyGetDialogQuery } from 'services/api/dialogsApi';
import { useGetTagsQuery } from 'services/api/tagsApi';
import { errorAlert } from 'ui-kit/Alert';
import { getErrorMessage, is403ErrorStatus } from 'utils/error';
import { isBlank } from 'utils/storeUtils';

const useDialogData = ({
  id,
  alias,
  scenarioId,
  characterName,
  isAdminPanel,
  updT,
  loading,
  setLoading,
  allTags,
  setAllTags,
  dialog,
  setDialog,
  scenario,
  setScenario,
  game,
  setGame,
  batches,
  setBatches,
  setMarketingData,
  setTotalBatchesNumber,
  setCustomDialogsBatchesNumber,
}) => {
  const [shouldFetchTags, setShouldFetchTags] = useState(false);
  const [dialogLimits, setDialogLimits] = useState(null);
  const navigate = useNavigate();
  const { data: tagsData } = useGetTagsQuery(undefined, {
    skip: !shouldFetchTags,
  });

  const [getDialog] = useLazyGetDialogQuery();
  const [getDialogByAlias] = useLazyGetDialogByScenarioAliasQuery();

  useEffect(() => {
    if (tagsData) {
      setAllTags(tagsData);
    }
  }, [tagsData, setAllTags]);

  const loadTags = useCallback(() => {
    setShouldFetchTags(true);
  }, []);

  const loadDialogData = useCallback(async (dialogId, alias) => {
    try {
      let result;
      if (alias) {
        result = await getDialogByAlias({
          alias,
          scenarioId,
          characterName,
          isAdminPanel
        }).unwrap();
      } else if (dialogId) {
        result = await getDialog(dialogId).unwrap();
      }

      if (!isBlank(result)) {
        const { dialog, scenario, game, batches } = result;
        setDialog(dialog);
        setScenario(scenario);
        setGame(game);
        setBatches(batches ?? []);
      }
    } catch (error) {
      if (is403ErrorStatus(error)) {
        const errorMessage = getErrorMessage(error);
        errorAlert(errorMessage);
        navigate(GUEST_ROUTES.ROOT());
      }
      throw error;
    } finally {
      setLoading(false);
    }
  }, [
    scenarioId,
    characterName,
    isAdminPanel,
    getDialog,
    getDialogByAlias,
    setDialog,
    setScenario,
    setGame,
    setBatches,
    setLoading
  ]);

  const loadAdditionalData = useCallback(async () => {
    try {
      const [marketingData, batchesData, dialogLimitsData] = await Promise.all([
        DialogsAPI.getLazyQuestParams(),
        DialogsAPI.getBatchesNumber(),
        DialogsAPI.getDialogLimits()
      ]);

      setMarketingData(marketingData);
      setTotalBatchesNumber(batchesData.totalBatchesNumber);
      setCustomDialogsBatchesNumber(batchesData.customDialogsUserBatchesNumber);
      setDialogLimits(dialogLimitsData);
    } catch (error) {
      throw error;
    }
  }, [setMarketingData, setTotalBatchesNumber, setCustomDialogsBatchesNumber]);

  useEffect(() => {
    if (!id && !alias) {
      return;
    }

    setLoading(true);
    loadTags();
    loadDialogData(id, alias);
  }, [id, alias, updT, loadTags, loadDialogData, setLoading]);

  useEffect(() => {
    if (game?.publicName) {
      loadAdditionalData();
    }
  }, [game?.publicName, loadAdditionalData]);

  return { loading, allTags, dialog, scenario, game, batches, dialogLimits };
};

export default useDialogData;
